import React, { useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import useLanguage from '@op/hooks/useLanguage';
import { echartsContent } from '@op/utils';
import { formatDateFunc } from '@op/utils/time';
import { reviewsFormat } from '@op/utils/value';
import { formatTimeXAxisText } from '@op/utils/chart';
import { downloadAction } from '@op/pages/download/utils';
import './index.scss';
import { metricsIconMap } from '@op/components/FilterCommon/widgets/MetricsSelect/config';
import { barIcon, lineIcon } from '@op/constants/chart';
import { NO_DATE } from '@op/constants';
import { PeriodType } from '../../SameStartDate/FilterInfo';

interface IChart {
  loading: boolean;
  height?: number;
  xAxisData: string[];
  yAxisData: {
    name: string;
    color: string;
    data: number[];
    game_id: string;
  }[];
  gameData: any;
  filter: any;
  valueFormat?: (value: number) => string | number;
  item: {
    text: string;
    value: string;
    icon: string;
  };
  isPercent?: boolean;
  yAxisLimit?: {
    [key: string]: any;
  };
  tableData: any;
  isEmpty: boolean;
  axisLength: number;
  id: string;
  logBtnId: string;
  dateType: string;
  singleTimeline: boolean;
  period?: PeriodType;
  hasEmptyTitle?: boolean;
  emptyDescText?: string;
  hasEmptyImg?: boolean;
}
const Chart = (props: IChart) => {
  const {
    loading,
    height,
    xAxisData,
    yAxisData,
    gameData,
    filter,
    valueFormat = (value: number) => reviewsFormat(value),
    item = {},
    isPercent = false,
    yAxisLimit = {},
    tableData,
    isEmpty,
    axisLength,
    id,
    logBtnId,
    dateType,
    singleTimeline,
    period,
    emptyDescText,
    hasEmptyTitle = true,
    hasEmptyImg = true,
  } = props;
  const { formatMessage } = useLanguage();
  const xAxisIntervalLimit = 8;
  const { text: title, icon, value } = item || {};
  const iconImg = icon ? metricsIconMap[icon] : '';
  const [chartType, setChartType] = useState<string>('line');
  const hideDate = !period?.isPeriod && singleTimeline;

  const options = useMemo(() => {
    if (!isEmpty) {
      const legendData: {
        name: string;
        itemStyle: {
          color: string;
        };
      }[] = [];
      const seriesData: {
        itemStyle?: any;
        barMaxWidth?: string;
        barGap?: string;
        label?: any;
        name: string;
        type: string;
        stack?: string;
        data: (number | null)[];
        xAxisIndex?: number;
      }[] = [];

      yAxisData.forEach((item, index) => {
        const { name, color, data, game_id } = item;
        seriesData.push({
          name: game_id + index,
          type: chartType,
          data,
          itemStyle: { color },
          stack: singleTimeline && chartType === 'bar' ? 'total' : '',
        });
        legendData.push({
          name: game_id + index,
          itemStyle: { color },
        });
      });

      let xAxis = xAxisData.map((item, index) => {
        const { color, data } = item;
        while (data.length < axisLength) {
          data.push('-');
        }
        const offset = (index + 1) * 15;
        return {
          type: 'category',
          data,
          axisLine: {
            onZero: false,
            lineStyle: {
              color,
              width: 0,
            },
            symbol: ['circle', 'none'],
            symbolSize: [8, 8],
            symbolOffset: [-10, 0],
          },
          axisLabel: {
            interval: axisLength > xAxisIntervalLimit ? 'auto' : 0,
            fontSize: 12,
            formatter: (value: any) => {
              if (value === '-') {
                return '-';
              }
              return formatTimeXAxisText(value, filter?.date_type);
            },
            margin: -5,
            color: '#838697',
          },
          axisTick: {
            alignWithLabel: true,
            show: false,
          },
          emphasis: {
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 1,
              borderType: 'solid',
              opacity: 1,
            },
          },
          position: 'bottom',
          offset,
        };
      });

      if (singleTimeline) {
        const { isPeriod, startPeriod } = period || {};
        xAxis = {
          type: 'category',
          data: xAxisData[0].data,
          axisLabel: {
            interval: axisLength > xAxisIntervalLimit ? 'auto' : 0,
            fontSize: 12,
            formatter: (value: any, index: number) => {
              if (isPeriod) {
                return dateType === 'monthly'
                  ? `M${index + (startPeriod ?? 1)}`
                  : `D${index + (startPeriod ?? 1)}`;
              }
              if (value === '-') {
                return '-';
              }
              return formatTimeXAxisText(value, filter?.date_type);
            },
            color: '#838697',
          },
          axisTick: {
            alignWithLabel: true,
            show: false,
          },
          emphasis: {
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 1,
              borderType: 'solid',
              opacity: 1,
            },
          },
          position: 'bottom',
        };
      }
      // const sortMap = getGameDataIndex(gameData);
      // legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));

      const option = {
        tooltip: {
          trigger: 'axis',
          borderWidth: 0,
          padding: 0,
          confine: true,
          extraCssText: 'z-index:8',
          formatter: (data: any[]) =>
            formatChartParamsDate({
              params: data,
              tablesConfigFunc: (records: Array<any>) => {
                let columns = [
                  {
                    key: 'text',
                    header: formatMessage({ id: 'Game' }),
                  },
                  {
                    key: 'date',
                    weight: 'bold',
                    header: formatMessage({ id: 'Date' }),
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: title }),
                  },
                ];

                if (hideDate) {
                  columns = columns.filter((val) => val.key !== 'date');
                }

                return [
                  {
                    showHeader: true,
                    records,
                    columns,
                  },
                ];
              },
              dealwithRecords: (records: Array<any>) =>
                records
                  .map((item, index) => {
                    const { time } = data[index].data;
                    let timeText = '';
                    if (filter?.date_type) {
                      timeText = formatDateFunc(filter?.date_type)(time);
                    }
                    return {
                      ...item,
                      date: timeText,
                    };
                  })
                  .sort((a, b) => b.originValue - a.originValue),
              valueFormat,
              textShowFun: (marker: string, seriesName: string, data) => {
                const gameId = seriesName.slice(0, -1);
                const { entity_name, cover } = gameData?.[gameId] || {};
                return `<span class="chart-common-formatter-marker dbo-game-comparison-chart-tooltip">${marker}<div class="game-icon" style="background-image: url(${cover});">
                </div> ${entity_name}</span>`;
              },
            }),
        },
        legend: {
          data: legendData,
          formatter: (name) => {
            const gameId = name.slice(0, -1);
            return gameData?.[gameId]?.entity_name;
          },
          bottom: '0',
        },
        xAxis,
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              if (!isPercent) {
                return echartsContent(value);
              }
              const max = yAxisLimit?.max ?? 1;
              const decimalPlace = max === 1 ? 100 : 1;
              return `${(value * decimalPlace).toFixed(0)}%`;
            },
            fontSize: 11,
          },
          ...(singleTimeline && chartType === 'bar' ? {} : yAxisLimit),
        },
        series: seriesData,
        grid: {
          containLabel: true,
          bottom: singleTimeline ? 30 : 30 + 10 * legendData.length,
          left: 0,
          right: 5,
        },
      };

      return option;
    }
    return {};
  }, [
    isEmpty,
    yAxisData,
    xAxisData,
    singleTimeline,
    yAxisLimit,
    chartType,
    axisLength,
    filter,
    period,
    dateType,
    valueFormat,
    formatMessage,
    title,
    gameData,
    isPercent,
  ]);

  return (
    <CardCommon
      id={id}
      style={{ scrollMarginTop: 20 }}
      hasDownload={!isEmpty}
      handleDownloadOnClick={() => {
        downloadAction({
          key: 'ComparisonGameChart',
          fileName: `Compare Competitors ${title}`,
          filter,
          data: tableData,
          auxiliaryParams: {
            key: id,
            dateType,
          },
        });
      }}
      className="dbo-game-comparison-chart"
      operations={[barIcon, lineIcon]}
      handleOperationsOnClick={(item) => {
        setChartType(item.symbol);
      }}
      defaultOperation={chartType}
      title={
        <div className="dbo-game-comparison-chart-title">
          {iconImg && (
            <img
              src={iconImg}
              className="dbo-game-comparison-chart-title-icon"
            ></img>
          )}
          {formatMessage({ id: title || NO_DATE.EMPTY })}
        </div>
      }
      logBtnId={logBtnId}
      extraInfo={id}
    >
      <EChartCommon
        loading={loading}
        height={height}
        empty={isEmpty}
        options={options}
        optionsProps={{ excludeOptions: ['axisPointer'], type: chartType }}
        hasEmptyTitle={hasEmptyTitle}
        emptyDescText={emptyDescText}
        hasEmptyImg={hasEmptyImg}
      />
    </CardCommon>
  );
};

export default Chart;
